

























































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Cropper, CircleStencil } from 'vue-advanced-cropper';

import eventBus from '@/utils/eventBus';
import { isRemoteLoading } from '@/utils/remote-loading';
import { openCenterModal } from '@/utils/modal';

import FlexButton from '@/components/FlexButton/FlexButton.vue';
import Radio from '@/components/Radio/Radio.vue';
import ZoomSlider from '@/components/ZoomSlider/ZoomSlider.vue';

import BrandedCoinPreview from './BrandedCoinPreview.vue';
import { iBondary, iViewport, iResult } from './CropModalTypes';

enum BackgroundColors {
  WHITE = 'white',
  BLACK = 'black'
}

interface RadioValue {
  label: string;
  value: BackgroundColors;
}

@Component({
  components: {
    FlexButton,
    Radio,
    Cropper,
    CircleStencil,
    ZoomSlider,
    BrandedCoinPreview
  }
})
export default class CropModal extends Vue {
  @Prop({ default: '50%' }) radius?: string;
  @Prop({ default: '#6B6B6B' }) borderColor?: string;
  @Prop({ default: 1 }) maxZoom?: number;
  @Prop() initialZoom?: number;
  @Prop({ required: false }) boundaryOptions!: iBondary;
  @Prop({ required: false }) viewportOptions!: iViewport;
  @Prop({ required: false }) initialState!: any;
  @Prop({ required: false }) src!: string;
  @Prop({ default: '1' }) minZoom!: string;
  @Prop({ default: false }) greenButton!: boolean;
  @Prop({ default: false }) scaleImage!: boolean;
  @Prop({ default: false }) scaleCanvas!: boolean;
  @Prop({ default: false }) disableBackground!: boolean;
  @Prop({ default: '' }) title!: string;
  @Prop({ default: null }) returnTo?: [string, ...any[]];
  @Prop({ default: '' }) actualImage!: string;
  @Prop({ default: '' }) imageId!: string;
  @Prop({ default: false }) brandedCoinShape?: boolean;
  @Prop({ default: false }) brandedCoinColor?: boolean;
  @Prop({ default: BackgroundColors.WHITE })
  @Prop({ default: false })
  readonly roundedCropWrapper?: boolean;
  defaultBackground!: BackgroundColors;
  @Prop({ default: 1 }) aspectRatio!: number;

  top: number = 0;

  radioValues: RadioValue[] = [
    {
      label: 'White',
      value: BackgroundColors.WHITE
    },
    {
      label: 'Black',
      value: BackgroundColors.BLACK
    }
  ];

  img: string = '';
  background: BackgroundColors = this.initialState
    ? this.initialState.background
    : this.defaultBackground || '';
  backgroundClass: BackgroundColors = this.initialState
    ? this.initialState.background
    : this.defaultBackground || '';
  loading: boolean = true;
  zoom = 0;
  modified = false;
  position = {
    left: 0,
    top: 0
  };

  @Watch('background')
  backgroundDidChange() {
    this.onBackgroundChange();
  }

  get modalStyle() {
    if (!isRemoteLoading() || this.$mobileCheck()) return {};

    return {
      position: 'absolute',
      left: `calc(50% - 160px)`,
      top: `${this.top}px`,
      transform: 'translateX(-50%)'
    };
  }

  changeBackground(newValue: BackgroundColors) {
    this.background = newValue;
    this.modified = true;
  }

  onZoom(value: number) {
    const cropper = this.$refs.cropper as any;

    if (cropper) {
      const { minHeight, minWidth } = cropper.sizeRestrictions;
      const { width, height } = cropper.imageSize;

      const [dimension, minDimension] =
        height < width ? [height, minHeight] : [width, minWidth];

      const totalDimension = dimension + minDimension;
      const zoom =
        value === 1
          ? 2
          : ((1 - this.zoom) * totalDimension) / ((1 - value) * totalDimension);

      cropper.zoom(zoom);
    }
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get boundaryStyle() {
    const { width, height } = this.boundaryOptions;

    let styles: Record<string, any> = {
      width: `${width}px`,
      height: `${height}px`,
      '--crop-radius': this.radius,
      '--crop-border-color': this.borderColor
    };

    if (this.roundedCropWrapper) {
      styles = {
        ...styles,
        borderRadius: this.radius,
        overflow: 'hidden'
      };
    }

    return styles;
  }

  get stencilType() {
    if (this.viewportOptions.type === 'circle') return 'circle-stencil';

    return 'rectangle-stencil';
  }

  get boundary() {
    return this.boundaryOptions;
  }

  onChange() {
    const cropper = this.$refs.cropper as any;
    if (cropper) {
      if (cropper.imageSize.height < cropper.imageSize.width) {
        this.zoom =
          (cropper.imageSize.height - cropper.coordinates.height) /
          (cropper.imageSize.height - cropper.sizeRestrictions.minHeight);
      } else {
        this.zoom =
          (cropper.imageSize.width - cropper.coordinates.width) /
          (cropper.imageSize.width - cropper.sizeRestrictions.minWidth);
      }
    }
  }

  stencilSize({ boundaries }: any) {
    return {
      width: boundaries.width,
      height: boundaries.height
    };
  }

  async onBackgroundChange(
    cb?: (result: iResult) => void,
    initialValue?: BackgroundColors
  ) {
    this.$showLoaderWithLocker();
    const { cropper }: { cropper?: any } = this.$refs;
    const { coordinates } = cropper.getResult();
    const result = await this.normalizeImage(this.src, initialValue);
    this.img = result.url;
    if (cb) cb(result);
    else cropper.setCoordinates(coordinates);
    this.$hideLoader();
    this.backgroundClass = initialValue || this.background;
  }

  async closeModal(type: string, event: any) {
    const { cropper }: { cropper?: any } = this.$refs;
    let res = event;
    const {
      coordinates,
      canvas
    }: { canvas: HTMLCanvasElement; coordinates: any } = cropper.getResult();

    if (type === 'onSave') {
      res = {
        img: canvas.toDataURL('image/png', 0.95),
        cropState: {
          coordinates,
          background: this.background
        }
      };
    } else if (this.returnTo) {
      res = {
        img: this.actualImage,
        cropState: this.initialState,
        imageId: this.imageId
      };
    }

    if (this.returnTo) {
      openCenterModal(...this.returnTo);

      this.$nextTick(() => {
        eventBus.$emit(`cropmodal-${type}`, {
          ...res,
          original: this.src
        });
      });
    } else {
      eventBus.$emit('closeModal');
    }

    this.$emit(type, res);
  }

  scaleIt(source: HTMLImageElement, scaleFactor: number) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const w = Math.round(source.width * scaleFactor);
    const h = Math.round(source.height * scaleFactor);

    canvas.width = w;
    canvas.height = h;

    if (ctx) {
      ctx.drawImage(source, 0, 0, w, h);
    }

    return canvas;
  }

  normalizeImage(
    src: string,
    initialValue?: 'white' | 'black'
  ): Promise<iResult> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.setAttribute('crossorigin', 'anonymous'); // works for me

      img.onload = () => {
        const { width, height } = img;

        if (this.scaleCanvas) {
          const maxDimension = Math.max(width, height);
          const minDimension = Math.min(width, height);

          const offset = Math.min(
            3,
            Math.min(maxDimension / minDimension, 1.25)
          );

          const canvasSize = maxDimension * offset;

          canvas.width = canvasSize;
          canvas.height = canvasSize;
        } else {
          canvas.width = width;
          canvas.height = height;
        }

        if (ctx) {
          if (!this.disableBackground) {
            ctx.fillStyle = initialValue || this.background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          const scaleFactor = 0.5;

          const scaledImage = this.scaleCanvas
            ? img
            : this.scaleIt(img, scaleFactor);

          ctx.drawImage(
            scaledImage,
            Math.round((canvas.width - img.width) / 2),
            Math.round((canvas.height - img.height) / 2),
            img.width,
            img.height
          );
        }

        resolve({
          width: canvas.width,
          height: canvas.height,
          url: canvas.toDataURL('image/png', 0.95)
        });
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  onReady() {
    if (!this.initialState && !this.modified) {
      const zoomValue =
        typeof this.initialZoom === 'number' ? this.initialZoom : 0.5;

      this.onZoom(zoomValue);
    }
  }

  updateModalPosition() {
    try {
      const modal = this.$refs.modal as HTMLElement;
      if (!modal) return;

      const scrollElement = window.parent.document.getElementById(
        'main-content'
      );

      const parentScrollY = scrollElement ? scrollElement.scrollTop : 0;
      const viewportHeight = window.parent.innerHeight;
      const modalHeight = modal.offsetHeight || 0;

      const iframeRects = window.frameElement.getBoundingClientRect();
      const iframeOffsetTop = iframeRects.top || 0;

      const iframeTop = iframeRects.height / 2;

      const resetTopCrop = -(iframeTop + iframeOffsetTop);

      const top = resetTopCrop + viewportHeight / 2 - modalHeight / 2;

      this.top = top * -1 > iframeTop ? -iframeTop : top;
    } catch (err) {
      console.error('[CenteredModal] Failed to update position:', err);
    }
  }

  messageUpdateModalPosition(event: MessageEvent) {
    if (event.data.type === 'updateModalPosition') {
      this.updateModalPosition();
    }
  }

  mounted() {
    this.updateModalPosition();

    window.addEventListener('resize', this.updateModalPosition);
    window.addEventListener('message', this.messageUpdateModalPosition);

    const setCoordinates = () => {
      const cropper = this.$refs.cropper as any;

      let coordinates = null;

      if (this.initialState && this.initialState.coordinates) {
        coordinates = this.initialState.coordinates;
      }

      if (coordinates) {
        cropper.setCoordinates(coordinates);
      }
    };

    if (this.src) {
      if (this.scaleImage) {
        this.onBackgroundChange(
          setCoordinates,
          this.initialState ? this.initialState.background : false
        );
      } else {
        this.img = this.src;

        setCoordinates();
      }
    }
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.updateModalPosition);
    window.removeEventListener('message', this.messageUpdateModalPosition);
  }
}
